<template>
  <v-parallax class="hero" src="/img/microneedling.webp" alt="Beauty and skincare treatments">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2 animate-text">reveal your natural glow with</div>
        <div class="text-center text-h2 pa-2 animate-title">expert skincare treatments</div>
      </div>
      <div class="text-center">
        <v-btn
          color="primary"
          :href="bookingHeroUrl"
          size="large"
          class="book-now-button animate-button"
          elevation="4">
          Book now!
        </v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose-beauty">
    <div class="full-width-header">Why choose our Beauty &amp; Skincare Services?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>
      <div class="text-center">
        <v-btn
          color="primary"
          :href="bookingHeroUrl"
          size="large"
          class="book-now-button animate-button"
          elevation="4">
          Book now!
        </v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by Our Clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featuredReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text">"{{ review.quote }}"</p>
        <div class="quote-author">{{ review.name }}</div>
      </div>
      <div class="text-center">
        <v-btn
          color="primary"
          :href="bookingHeroUrl"
          size="large"
          class="book-now-button animate-button"
          elevation="4">
          Book now!
        </v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of professional Skincare?"
    :benefits="benefits"
    :booking-url="bookingHeroUrl" />

  <section id="beauty-services">
    <div class="full-width-header">Our Beauty &amp; Skincare Services</div>
    <div class="content pt-6 pb-10">
      <p class="section-intro text-center">
        From deep cleansing facials to advanced rejuvenation, each treatment is tailored to your unique skin needs.
      </p>
      <div ref="serviceList" class="service-list mt-8 d-flex flex-wrap justify-space-around ga-12 px-8">
        <ServiceItemComponent
          v-for="serviceItem in serviceList"
          :key="serviceItem.title"
          :title="serviceItem.title"
          :book-now-link="serviceItem.bookNowLink"
          :sub-title="serviceItem.subTitle"
          :link-target="serviceItem.linkTarget"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel radiant?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Take the first step towards healthier, glowing skin. Book your beauty treatment today and let our specialists craft the perfect experience for you.
      </p>
      <div class="text-center">
        <v-btn
          color="primary"
          :href="bookingHeroUrl"
          size="large"
          class="book-now-button animate-button"
          elevation="4">
          Book now!
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import { initServiceListAlignment } from '@/utils/serviceItemAlignment';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'BeautySkincareView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  computed: {
    bookingHeroUrl(): string {
      return BookingUrls.BeautyTreatment.Default;
    }
  },

  data() {
    return {
      alignmentCleanup: null as null | (() => void),
      whyChoose: [
        {
          title: 'Personalised Skin Analysis',
          description: 'Every treatment begins with a thorough assessment so we can tailor the perfect plan for your skin type and goals.'
        },
        {
          title: 'Advanced Techniques',
          description: 'From microneedling to probiotic detox facials, we use the latest methods and professional-grade products for visible results.'
        },
        {
          title: 'Relaxing & Results-Driven',
          description: 'Enjoy a calming, spa-like experience while benefiting from treatments that deliver real, lasting improvements.'
        }
      ],
      starsImage: '/img/five-stars.png',
      benefits: [
        {
          title: 'Deeply Cleansed Skin',
          description: 'Professional treatments remove impurities, unclog pores, and leave your complexion feeling fresh and renewed.'
        },
        {
          title: 'Visible Rejuvenation',
          description: 'Advanced techniques stimulate collagen production and cell renewal for smoother, firmer, and more youthful-looking skin.'
        },
        {
          title: 'Even Skin Tone',
          description: 'Targeted peels and vitamin-infused treatments help reduce dark spots, pigmentation, and uneven texture.'
        },
        {
          title: 'Long-Lasting Hydration',
          description: 'Intensive moisturising treatments replenish your skin\'s moisture barrier, keeping it soft and supple for longer.'
        }
      ],
      featuredReviews: [
        {
          name: 'Arthur M',
          quote:
            'Excellent microneedling and deep probiotic detox treatments with Bianca. My skin felt smoother, deeply cleansed, and refreshed, and the whole experience was relaxing, professional, and very effective. My friends noticed a visible improvement straight away. I will definitely book again.'
        }
      ],
      serviceList: [
        {
          title: 'Skin Assessment',
          bookNowLink: BookingUrls.BeautyTreatment.SkinAssessment,
          subTitle: 'Discover the unique nuances of your skin with an expert skin assessment. This personalised consultation examines your skin type, condition, and specific concerns to identify the perfect approach for your skincare journey.',
          linkTarget: '_self'
        },
        {
          title: 'Deep Cleansing Probiotic Detox',
          bookNowLink: BookingUrls.BeautyTreatment.DeepCleansingProbioticDetox,
          subTitle: 'A purifying facial powered by probiotics to restore your skin\'s natural balance, remove impurities, and reveal a fresh, radiant complexion.',
          linkTarget: '_self'
        },
        {
          title: 'Microneedling',
          bookNowLink: BookingUrls.BeautyTreatment.Microneedling,
          subTitle: 'Experience a gentle approach to skin rejuvenation with this specialised facial treatment. Microneedling uses fine needles to help refresh and revitalise your complexion for a smoother, more even appearance.',
          linkTarget: '_self'
        },
        {
          title: 'Dermabrasion Peel',
          bookNowLink: BookingUrls.BeautyTreatment.DermabrasionPeel,
          subTitle: 'Experience a new level of skin refinement with this gentle facial treatment designed to leave your complexion feeling smooth and refreshed, revealing a natural radiance.',
          linkTarget: '_self'
        },
        {
          title: 'Velvet Lip Moisturise',
          bookNowLink: BookingUrls.BeautyTreatment.VelvetLipMoisturise,
          subTitle: 'Intensive hydration and visibly smoother lips with advanced Bio-Filler Lip technology. Targets vertical lip lines, replenishes moisture, and delivers a natural, subtle plumping effect.',
          linkTarget: '_self'
        },
        {
          title: 'Bright Renew Peel for Melasma',
          bookNowLink: BookingUrls.BeautyTreatment.BrightRenewPeel,
          subTitle: 'Our safest and most advanced depigmenting peel that targets only areas affected by excess melanin. Visibly reduces dark spots with zero rebound effect, safe for all skin types and seasons.',
          linkTarget: '_self'
        },
        {
          title: 'Revitalizing Treatment with Vitamin C',
          bookNowLink: BookingUrls.BeautyTreatment.VitaminCTreatment,
          subTitle: 'Features three types of Vitamin C combined with Gold Microparticles for the ultimate treatment in brightness, reversing pigmentation, and delivering a luxurious, youthful glow.',
          linkTarget: '_self'
        },
        {
          title: 'The Signature Probiotic Detox',
          bookNowLink: BookingUrls.BeautyTreatment.SignatureProbioticDetox,
          subTitle: 'Experience the power of probiotics with this revitalising facial that purifies and restores your skin\'s natural balance, customised to your needs for a deeply renewed complexion.',
          linkTarget: '_self'
        },
        {
          title: 'Radiant Cleanse & Hydrate',
          bookNowLink: BookingUrls.BeautyTreatment.RadiantCleanseHydrate,
          subTitle: 'Ultrasonic cleanse and hydration boost for an instant glow. The perfect pick-me-up for your skin.',
          linkTarget: '_self'
        }
      ]
    };
  },

  mounted() {
    const list = this.$refs.serviceList as HTMLElement | undefined;

    if (!list) {
      return;
    }

    this.alignmentCleanup = initServiceListAlignment(list);
  },

  beforeUnmount() {
    if (this.alignmentCleanup) {
      this.alignmentCleanup();
    }
  }
});
</script>

<style scoped>
/* #beauty-services {
  padding-top: 0;
} */
</style>
